// Finder — the Telescope-style two-pane content-finder modal.
//
// The app's "find in content" surface (.claude/analysis/fuzzy-finder.md): a prompt,
// a results LIST with per-character match-range highlight (left), and a PREVIEW
// pane showing the hit line centred in its surrounding content, highlighted
// (right). Up/Down moves the selection and the preview FOLLOWS. A leading `#`
// switches to content-only mode (the Sublime sigil) live in the same input.
//
// This is the finder's SHELL half (docs §4c): it owns only view-state (the
// query string, the selected index), never domain state. It ranks a
// `FinderDoc[]` snapshot — a projection the host hands in via props — through
// the pure read-model (finderIndex.ts). Selecting a hit reports a `FinderTarget`
// UP; the host navigates. The finder emits no domain events.
//
// A11y: the hard modal contract (focus trap, scrim, Esc-to-dismiss,
// overlay-click, focus return) is Radix's (the same primitive Dialog wraps). On
// top of it the finder is a WAI-ARIA combobox: DOM focus stays on the prompt
// field; the active option is named via aria-activedescendant, never focused —
// the CommandPalette precedent. Pure/props-driven: no effects, no I/O.

import { Trans, useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMemo, useState } from "react";
import { Kbd } from "../Kbd/Kbd.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./Finder.css";
import {
    buildCandidates,
    type FinderDoc,
    type FinderHit,
    type FinderKind,
    type FinderTarget,
    interpretQuery,
    rankHits,
    segmentByRanges,
    type TextSegment,
    targetOf,
} from "./finderIndex.js";

export type FinderProps = {
    // Whether the modal is open. Controlled — the host owns this (frame overlay).
    readonly open: boolean;
    // Reports every requested open/close (Esc, overlay click, a jump). The host
    // applies it to the frame overlay.
    readonly onOpenChange: (open: boolean) => void;
    // The content snapshot to search — projected by the host from resource
    // state. Empty while the index loads (see `loading`).
    readonly docs: readonly FinderDoc[];
    // The index read is in flight — the results pane shows a calm loading line
    // instead of a false "No matches".
    readonly loading?: boolean;
    // Selecting a hit reports its navigation target UP; the host opens it.
    readonly onJump: (target: FinderTarget) => void;
    // The prompt's seed text (default empty). A fresh value each open, since the
    // body remounts when the modal opens.
    readonly initialQuery?: string;
};

// Render a string with its matched ranges marked. Segments partition the string
// left-to-right, so the running character offset is a stable key.
const Highlighted = ({
    segments,
}: {
    readonly segments: readonly TextSegment[];
}) => {
    let offset = 0;
    return (
        <>
            {segments.map((segment) => {
                const key = `${offset}:${segment.text}`;
                offset += segment.text.length;
                return segment.matched ? (
                    <mark key={key} className="bilda-finder__mark">
                        {segment.text}
                    </mark>
                ) : (
                    <span key={key}>{segment.text}</span>
                );
            })}
        </>
    );
};

// The preview pane: the selected hit's resource, every line rendered, the hit
// line emphasised and highlighted, the rest as dimmed surrounding context.
const Preview = ({
    hit,
    docs,
    kindLabel,
}: {
    readonly hit: FinderHit | undefined;
    readonly docs: readonly FinderDoc[];
    readonly kindLabel: (kind: FinderKind) => string;
}) => {
    const { t } = useLingui();
    if (hit === undefined)
        return (
            <div className="bilda-finder__preview bilda-finder__preview--empty">
                <Trans>No preview</Trans>
            </div>
        );

    const doc = docs.find((d) => d.resourceId === hit.candidate.resourceId);
    if (doc === undefined) return null;

    const activeLineIndex =
        hit.candidate.field.type === "content"
            ? hit.candidate.field.lineIndex
            : -1;
    const titleIsHit = hit.candidate.field.type === "title";

    return (
        <section className="bilda-finder__preview" aria-label={t`Preview`}>
            <header className="bilda-finder__preview-head">
                <span className="bilda-finder__chip">
                    {kindLabel(doc.kind)}
                </span>
                <h3 className="bilda-finder__preview-title">
                    {titleIsHit ? (
                        <Highlighted
                            segments={segmentByRanges(
                                doc.title,
                                hit.match.ranges,
                            )}
                        />
                    ) : (
                        doc.title
                    )}
                </h3>
            </header>
            <div className="bilda-finder__preview-body">
                {doc.lines.map((line, index) => {
                    const active = index === activeLineIndex;
                    return (
                        <p
                            key={line.elementPath.join("/")}
                            className={`bilda-finder__line${active ? " bilda-finder__line--active" : ""}`}
                        >
                            <span className="bilda-finder__line-heading">
                                {line.heading}
                            </span>
                            <span className="bilda-finder__line-text">
                                {active ? (
                                    <Highlighted
                                        segments={segmentByRanges(
                                            line.text,
                                            hit.match.ranges,
                                        )}
                                    />
                                ) : (
                                    line.text
                                )}
                            </span>
                        </p>
                    );
                })}
            </div>
        </section>
    );
};

// The stateful two-pane body — mounted only while the modal is open (Content
// unmounts on close), so the query + selection reset to a fresh search each
// summon without an effect.
const FinderBody = ({
    docs,
    loading,
    onJump,
    onOpenChange,
    initialQuery,
}: {
    readonly docs: readonly FinderDoc[];
    readonly loading: boolean;
    readonly onJump: (target: FinderTarget) => void;
    readonly onOpenChange: (open: boolean) => void;
    readonly initialQuery: string;
}) => {
    const { t } = useLingui();
    const [query, setQuery] = useState(initialQuery);
    const [selected, setSelected] = useState(0);

    // The localized kind chip label. A user-facing string, so it lives here
    // (via the i18n macro), not in the pure read-model.
    const kindLabel = (kind: FinderKind): string =>
        kind === "source"
            ? t`Source`
            : kind === "material"
              ? t`Material`
              : t({ message: "Export", context: "resource kind" });

    const { mode, needle } = interpretQuery(query);
    const hits = useMemo(
        () => rankHits(needle, buildCandidates(docs, mode)),
        [needle, mode, docs],
    );

    // Clamp the selection into the current result set every render.
    const activeIndex =
        hits.length === 0 ? -1 : Math.min(selected, hits.length - 1);
    const activeHit = activeIndex >= 0 ? hits[activeIndex] : undefined;
    const optionId = (index: number) => `bilda-finder-opt-${index}`;

    const jumpTo = (hit: FinderHit) => {
        onJump(targetOf(hit.candidate));
        onOpenChange(false);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setSelected((i) => Math.min(i + 1, hits.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setSelected((i) => Math.max(i - 1, 0));
        } else if (event.key === "Enter" && activeHit !== undefined) {
            event.preventDefault();
            jumpTo(activeHit);
        }
    };

    return (
        <>
            <div className="bilda-finder__prompt">
                <input
                    className="bilda-finder__input"
                    type="text"
                    // biome-ignore lint/a11y/noAutofocus: a summoned finder takes focus by design
                    autoFocus
                    // The WAI-ARIA combobox pattern (the CommandPalette
                    // precedent): DOM focus stays on the field; the active option
                    // is named via aria-activedescendant, never focused.
                    role="combobox"
                    aria-label={t`Search across resources and content`}
                    aria-expanded={hits.length > 0}
                    aria-controls="bilda-finder-results"
                    aria-activedescendant={
                        activeIndex >= 0 ? optionId(activeIndex) : undefined
                    }
                    aria-autocomplete="list"
                    placeholder={t`Search titles and content — prefix # for content only`}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setSelected(0);
                    }}
                    onKeyDown={onKeyDown}
                />
                <span className="bilda-finder__mode">
                    {mode.type === "content" ? (
                        <Trans>content</Trans>
                    ) : (
                        <Trans>all</Trans>
                    )}
                </span>
                <Kbd keys={["mod", "shift", "F"]} />
            </div>

            <div className="bilda-finder__panes">
                {/* A div-based listbox: `role="listbox"` has no native HTML
                    element, so (per the CommandPalette precedent) a div avoids
                    the ul/li non-interactive-role conflict; options are div rows
                    the combobox field drives via aria-activedescendant. */}
                <div
                    className="bilda-finder__results"
                    id="bilda-finder-results"
                    role="listbox"
                    aria-label={t`Results`}
                >
                    {loading ? (
                        <p className="bilda-finder__no-results">
                            <Trans>Searching…</Trans>
                        </p>
                    ) : hits.length === 0 ? (
                        <p className="bilda-finder__no-results">
                            <Trans>No matches</Trans>
                        </p>
                    ) : (
                        hits.map((hit, index) => {
                            const active = index === activeIndex;
                            const isTitle =
                                hit.candidate.field.type === "title";
                            const heading =
                                hit.candidate.field.type === "content"
                                    ? hit.candidate.field.heading
                                    : t`Title`;
                            return (
                                // biome-ignore lint/a11y/useKeyWithClickEvents: the prompt input owns keyboard via aria-activedescendant; the option is a pointer target only (Enter is handled on the input — the CommandPalette precedent).
                                <div
                                    key={`${hit.candidate.resourceId}:${hit.candidate.field.type === "content" ? hit.candidate.field.elementPath.join("/") : "title"}`}
                                    id={optionId(index)}
                                    role="option"
                                    tabIndex={-1}
                                    aria-selected={active}
                                    className={`bilda-finder__row${active ? " bilda-finder__row--active" : ""}`}
                                    onMouseEnter={() => setSelected(index)}
                                    onClick={() => jumpTo(hit)}
                                >
                                    <span className="bilda-finder__chip">
                                        {kindLabel(hit.candidate.kind)}
                                    </span>
                                    <span className="bilda-finder__row-main">
                                        <span className="bilda-finder__row-title">
                                            {isTitle ? (
                                                <Highlighted
                                                    segments={segmentByRanges(
                                                        hit.candidate.text,
                                                        hit.match.ranges,
                                                    )}
                                                />
                                            ) : (
                                                hit.candidate.resourceTitle
                                            )}
                                        </span>
                                        {!isTitle && (
                                            <span className="bilda-finder__row-excerpt">
                                                <Highlighted
                                                    segments={segmentByRanges(
                                                        hit.candidate.text,
                                                        hit.match.ranges,
                                                    )}
                                                />
                                            </span>
                                        )}
                                    </span>
                                    <span className="bilda-finder__row-heading">
                                        {heading}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>

                <Preview hit={activeHit} docs={docs} kindLabel={kindLabel} />
            </div>

            <footer className="bilda-finder__foot">
                <span>
                    <Kbd keys={["↑"]} /> <Kbd keys={["↓"]} />{" "}
                    <Trans>to navigate</Trans>
                </span>
                <span>
                    <Kbd keys={["Enter"]} /> <Trans>to jump to element</Trans>
                </span>
                <span>
                    <Kbd keys={["Esc"]} /> <Trans>to dismiss</Trans>
                </span>
            </footer>
        </>
    );
};

export const Finder = ({
    open,
    onOpenChange,
    docs,
    loading = false,
    onJump,
    initialQuery = "",
}: FinderProps) => {
    const { t } = useLingui();
    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="bilda-finder__scrim" />
                <DialogPrimitive.Content className="bilda-finder">
                    {/* Radix requires a Title for the dialog's accessible name;
                        the finder's visible surface is its prompt, so the title
                        is screen-reader-only. */}
                    <VisuallyHidden as="div">
                        <DialogPrimitive.Title>
                            {t`Find in content`}
                        </DialogPrimitive.Title>
                    </VisuallyHidden>
                    <FinderBody
                        docs={docs}
                        loading={loading}
                        onJump={onJump}
                        onOpenChange={onOpenChange}
                        initialQuery={initialQuery}
                    />
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};
